import _ from 'lodash';

import { TEAM_MEMBER_ROLES } from 'admin/constants/common';
import { IUser } from 'src/types';

const ACTIONS = {
	publish: 'publish',
	manageOwnStory: 'manage_own_story',
	manageOtherStories: 'manage_other_stories',
	watchAnalytics: 'watch_analytics',
	manageMembers: 'manage_members',
	delete: 'delete',
} as const;

export class RolesFacade {
	user: IUser;

	static actions = ACTIONS;

	constructor(user: IUser) {
		this.user = user;
	}

	static set user(val: IUser) {
		this.user = val;
	}

	private get isActiveTeamPrivate() {
		return this.user.activeTeam === '';
	}

	private get activeTeamRoles() {
		const teamId = this.user.activeTeam;
		const currentTeam = _.find(this.user.teams, { id: teamId });

		return _.get(currentTeam, 'teamMembers.roles') ?? [];
	}

	private get organization() {
		const organizationId = this.user.activeOrganization;
		return _.find(this.user.organizations, { id: organizationId });
	}

	public get isSubscriptionCanceled() {
		return !!this.organization?.subscriptionStatus && this.organization.subscriptionStatus === 'CANCELED';
	}

	public get isOrganizationManager() {
		return _.get(this.organization, 'organizationMembers.isManager', false);
	}

	public can = (action: typeof ACTIONS[keyof typeof ACTIONS], teamID: string = '') => {
		const targetTeamRoles = teamID ? this.getTeamRoles(teamID) : this.activeTeamRoles;

		if (this.isActiveTeamPrivate && teamID === '') {
			return true;
		}

		switch (action) {
			case RolesFacade.actions.publish:
				return this.isOrganizationManager || targetTeamRoles.includes(TEAM_MEMBER_ROLES.PUBLISH);
			case RolesFacade.actions.manageOwnStory:
				return this.isOrganizationManager || targetTeamRoles.includes(TEAM_MEMBER_ROLES.STORY_MANAGE_OWN);
			case RolesFacade.actions.manageOtherStories:
				return this.isOrganizationManager || targetTeamRoles.includes(TEAM_MEMBER_ROLES.STORY_MANAGE_OTHERS);
			case RolesFacade.actions.delete:
				return this.isOrganizationManager || targetTeamRoles.includes(TEAM_MEMBER_ROLES.STORY_DELETE);
			case RolesFacade.actions.watchAnalytics:
				return this.isOrganizationManager || targetTeamRoles.includes(TEAM_MEMBER_ROLES.ANALYTICS);
			case RolesFacade.actions.manageMembers:
				return targetTeamRoles.includes(TEAM_MEMBER_ROLES.MANAGER_MEMBERS);
			default:
				return false;
		}
	};

	public getTeamRoles = (teamId: string) => {
		const targetTeam = _.find(this.user.teams, { id: teamId });

		return _.get(targetTeam, 'teamMembers.roles') ?? [];
	};
}
